import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { listClientCommunities } from "../../api/clientCommunitiesApi";
import { getClientPortalView } from "../../api/clientPortalApi";
import {
  ClientPortalError,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDate, formatStatusLabel } from "../../utils/clientPortalFormat";
import ClientContentPublishBadges from "../../components/clientPortal/ClientContentPublishBadges";
import "../../styles/clientCommunitiesList.css";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "HIDDEN", label: "Hidden" },
  { value: "ARCHIVED", label: "Archived" },
];

const STATUS_BADGE_CLASS = {
  ACTIVE: "ccl-card__badge--active",
  DRAFT: "ccl-card__badge--draft",
  HIDDEN: "ccl-card__badge--hidden",
  ARCHIVED: "ccl-card__badge--archived",
};

function SkeletonGrid() {
  return (
    <div className="ccl-skeleton-grid">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="ccl-skeleton-card">
          <div className="ccl-skeleton-card__hero" />
          <div className="ccl-skeleton-card__body">
            <div className="ccl-skeleton-bar ccl-skeleton-bar--title" />
            <div className="ccl-skeleton-bar ccl-skeleton-bar--text" />
            <div className="ccl-skeleton-bar ccl-skeleton-bar--text-long" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryMetrics({ summary }) {
  if (!summary) return null;

  return (
    <div className="ccl-metrics">
      <div className="ccl-metric">
        <span className="ccl-metric__label">Total</span>
        <span className="ccl-metric__value">{summary.total ?? 0}</span>
      </div>
      <div className="ccl-metric ccl-metric--active">
        <span className="ccl-metric__label">Active</span>
        <span className="ccl-metric__value">{summary.active ?? 0}</span>
      </div>
      <div className="ccl-metric ccl-metric--draft">
        <span className="ccl-metric__label">Draft</span>
        <span className="ccl-metric__value">{summary.draft ?? 0}</span>
      </div>
      <div className="ccl-metric ccl-metric--featured">
        <span className="ccl-metric__label">Featured</span>
        <span className="ccl-metric__value">{summary.featured ?? 0}</span>
      </div>
    </div>
  );
}

function formatAddress(community) {
  const parts = [community.addressLine1, community.city, community.stateRegion].filter(Boolean);
  if (!parts.length) return "";
  const cityState = [community.city, community.stateRegion].filter(Boolean).join(", ");
  if (community.addressLine1) {
    return community.postalCode
      ? `${community.addressLine1}, ${cityState} ${community.postalCode}`
      : `${community.addressLine1}, ${cityState}`;
  }
  return community.postalCode ? `${cityState} ${community.postalCode}` : cityState;
}

function CommunityCard({ community, clientViewId }) {
  const paths = useClientPortalPaths();
  const location = formatAddress(community);
  const statusClass = STATUS_BADGE_CLASS[community.displayStatus] || "ccl-card__badge--draft";

  return (
    <article className="ccl-card">
      <div className="ccl-card__hero">
        {community.heroImageUrl ? (
          <img src={community.heroImageUrl} alt={community.communityName} loading="lazy" />
        ) : (
          <span className="ccl-card__hero-placeholder" aria-hidden="true">&#9971;</span>
        )}
        <div className="ccl-card__badges">
          <span className={`ccl-card__badge ${statusClass}`}>
            {formatStatusLabel(community.displayStatus)}
          </span>
          {community.isFeatured && (
            <span className="ccl-card__badge ccl-card__badge--featured">Featured</span>
          )}
        </div>
      </div>

      <div className="ccl-card__body">
        <h3 className="ccl-card__name">{community.communityName}</h3>

        {location && (
          <div className="ccl-card__location">
            <span className="ccl-card__location-icon" aria-hidden="true">&#128205;</span>
            {location}
          </div>
        )}

        {community.shortDescription && (
          <p className="ccl-card__description">{community.shortDescription}</p>
        )}

        <div className="ccl-card__publish-row">
          <ClientContentPublishBadges item={community} />
        </div>

        <div className="ccl-card__stats">
          <span className="ccl-card__stat">
            <span className="ccl-card__stat-icon" aria-hidden="true">&#9971;</span>
            <strong>{community.courseCount ?? 0}</strong> courses
          </span>
          <span className="ccl-card__stat">
            <span className="ccl-card__stat-icon" aria-hidden="true">&#128337;</span>
            Updated {formatClientDate(community.updatedAt)}
          </span>
        </div>
      </div>

      <div className="ccl-card__actions">
        <Link
          to={paths.viewSection(clientViewId, "communities", community.communityId)}
          className="ccl-card__action ccl-card__action--primary"
        >
          View Details
        </Link>
        {community.canManage && (
          <Link
            to={paths.viewSection(clientViewId, "communities", community.communityId, "edit")}
            className="ccl-card__action ccl-card__action--secondary"
          >
            Edit
          </Link>
        )}
      </div>
    </article>
  );
}

const ClientCommunitiesListPage = () => {
  const { clientViewId } = useParams();
  const [viewName, setViewName] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState("updated");
  const [page, setPage] = useState(1);
  const paths = useClientPortalPaths();

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const [viewBody, listBody] = await Promise.all([
        getClientPortalView(clientViewId),
        listClientCommunities(clientViewId, {
          page,
          pageSize: 25,
          search: search || undefined,
          status: status || undefined,
          featured: featuredOnly || undefined,
          sort,
        }),
      ]);
      setViewName(viewBody.clientView?.name || "Client View");
      setData(listBody);
    } catch (error) {
      setErrorMessage(error.message || "We could not load communities. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [clientViewId, page, search, status, featuredOnly, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary;

  return (
    <div className="ccl">
      <div className="ccl-header">
        <div className="ccl-header__left">
          <nav className="ccl-breadcrumb" aria-label="Breadcrumb">
            <Link to={paths.view(clientViewId)}>{viewName || "Client View"}</Link>
            <span className="ccl-breadcrumb__sep" aria-hidden="true">/</span>
            <span>Communities</span>
          </nav>
          <h1 className="ccl-header__title">Communities</h1>
          <p className="ccl-header__subtitle">
            Manage golf community content for your site
          </p>
        </div>
        {data?.canManage && (
          <div className="ccl-header__actions">
            <Link
              to={paths.viewSection(clientViewId, "communities", "new")}
              className="ccf-btn ccf-btn--primary"
            >
              + Add Community
            </Link>
          </div>
        )}
      </div>

      {errorMessage && <ClientPortalError message={errorMessage} />}

      {loading && !data ? (
        <SkeletonGrid />
      ) : data ? (
        <>
          <SummaryMetrics summary={summary} />

          <div className="ccl-toolbar">
            <div className="ccl-search">
              <span className="ccl-search__icon" aria-hidden="true">&#128269;</span>
              <input
                type="search"
                className="ccl-search__input"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Search by name, city, or description..."
              />
            </div>

            <span className="ccl-toolbar__divider" aria-hidden="true" />

            <select
              className="ccl-filter-select"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              className="ccl-filter-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="updated">Recently updated</option>
              <option value="name">Name A-Z</option>
              <option value="featured">Featured first</option>
              <option value="status">Status</option>
            </select>

            <button
              type="button"
              className={`ccl-toggle-chip ${featuredOnly ? "ccl-toggle-chip--active" : ""}`}
              onClick={() => {
                setPage(1);
                setFeaturedOnly((v) => !v);
              }}
            >
              {featuredOnly ? "★" : "☆"} Featured only
            </button>
          </div>

          {!loading && data.communities?.length === 0 ? (
            <div className="ccl-empty">
              <span className="ccl-empty__icon" aria-hidden="true">&#9971;</span>
              <h2 className="ccl-empty__title">No communities yet</h2>
              <p className="ccl-empty__text">
                No communities match your current filters. Try adjusting your search or add a new community to get started.
              </p>
            </div>
          ) : (
            <div className="ccl-grid">
              {data.communities?.map((community) => (
                <CommunityCard
                  key={community.communityId}
                  community={community}
                  clientViewId={clientViewId}
                />
              ))}
            </div>
          )}

          {data.pagination?.totalPages > 1 && (
            <div className="ccl-pagination">
              <button
                type="button"
                className="ccl-pagination__btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Previous
              </button>
              <span className="ccl-pagination__info">
                Page {data.pagination.page} of {data.pagination.totalPages}
              </span>
              <button
                type="button"
                className="ccl-pagination__btn"
                disabled={page >= data.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default ClientCommunitiesListPage;
